// Register-Skript für den Service Worker
// Diese Datei wird in main.tsx importiert

const VERSION_ENDPOINT = '/version.json';
const localSentinelKey = __APP_BUILD_ID__;
const SENTINEL_RELOAD_GUARD_KEY = 'sentinel-reload-guard';
const SENTINEL_RELOAD_MAX_ATTEMPTS = 2;
const SENTINEL_RELOAD_COOLDOWN_MS = 60_000;
let reloadTriggered = false;
const isIOSWebKit = detectIOSWebKit();

export function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/'
        });
        
        console.log('Service Worker erfolgreich registriert mit Scope:', registration.scope);
        
        // Event-Listener für Service Worker-Updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          console.log('Service Worker Update gefunden!');
          
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // Neue Version verfügbar - Update-Nachricht anzeigen
                console.log('Neue Version verfügbar! Bitte Seite neu laden.');
                
                // Optional: Hier Update-Banner/Toast anzeigen
                showUpdateBanner();
              }
            });
          }
        });
        
        // Event-Listener für Nachrichten vom Service Worker
        navigator.serviceWorker.addEventListener('message', (event) => {
          const { type, timestamp } = event.data;
          
          if (type === 'SYNC_COMPLETED') {
            console.log('Synchronisation abgeschlossen:', new Date(timestamp).toLocaleTimeString());
            
            // Event für den Queue-Context auslösen
            window.dispatchEvent(new CustomEvent('queueSyncCompleted', {
              detail: { timestamp }
            }));
          }
        });

        await checkAndRefreshIfSentinelChanged(registration);
        if (!isIOSWebKit) {
          setupRouteChangeSentinelCheck(registration);
        }
      } catch (error) {
        console.error('Service Worker Registrierungsfehler:', error);
      }
    });
  } else {
    console.warn('Service Worker wird vom Browser nicht unterstützt');
  }
}

function setupRouteChangeSentinelCheck(registration: ServiceWorkerRegistration) {
  const originalPushState = history.pushState.bind(history);
  const originalReplaceState = history.replaceState.bind(history);

  history.pushState = function (...args) {
    originalPushState(...args);
    void checkAndRefreshIfSentinelChanged(registration);
  };

  history.replaceState = function (...args) {
    originalReplaceState(...args);
    void checkAndRefreshIfSentinelChanged(registration);
  };

  window.addEventListener('popstate', () => {
    void checkAndRefreshIfSentinelChanged(registration);
  });
}

async function checkAndRefreshIfSentinelChanged(registration: ServiceWorkerRegistration) {
  if (reloadTriggered) return;

  try {
    const response = await fetch(VERSION_ENDPOINT, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache'
      }
    });

    if (!response.ok) {
      return;
    }

    const data = await response.json() as { sentinelKey?: string };
    const remoteSentinelKey = data.sentinelKey;

    if (!remoteSentinelKey || remoteSentinelKey === localSentinelKey) {
      clearSentinelReloadGuard();
      return;
    }

    if (!canTriggerSentinelReload(remoteSentinelKey)) {
      console.warn(
        `[Sentinel] Reload blockiert (Guard aktiv). local=${localSentinelKey}, remote=${remoteSentinelKey}`
      );
      return;
    }

    if (isIOSWebKit) {
      console.info(
        `[Sentinel] Neue Version erkannt (iOS WebKit): soft update ohne erzwungenen Reload. local=${localSentinelKey}, remote=${remoteSentinelKey}`
      );
      showUpdateBanner();
      return;
    }

    reloadTriggered = true;
    console.info(
      `[Sentinel] Neue Version erkannt. local=${localSentinelKey}, remote=${remoteSentinelKey}`
    );

    await registration.update();

    const cacheKeys = await caches.keys();
    await Promise.all(cacheKeys.map((key) => caches.delete(key)));

    window.location.reload();
  } catch (error) {
    console.warn('[Sentinel] Konnte Version nicht prüfen:', error);
  }
}

type SentinelReloadGuard = {
  remoteSentinelKey: string;
  attempts: number;
  lastReloadAt: number;
};

function canTriggerSentinelReload(remoteSentinelKey: string): boolean {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return true;
  }

  const now = Date.now();
  const current = readSentinelReloadGuard();

  if (!current || current.remoteSentinelKey !== remoteSentinelKey) {
    writeSentinelReloadGuard({
      remoteSentinelKey,
      attempts: 1,
      lastReloadAt: now,
    });
    return true;
  }

  if (current.attempts >= SENTINEL_RELOAD_MAX_ATTEMPTS) {
    return false;
  }

  if (now - current.lastReloadAt < SENTINEL_RELOAD_COOLDOWN_MS) {
    return false;
  }

  writeSentinelReloadGuard({
    remoteSentinelKey,
    attempts: current.attempts + 1,
    lastReloadAt: now,
  });
  return true;
}

function readSentinelReloadGuard(): SentinelReloadGuard | null {
  try {
    const raw = window.sessionStorage.getItem(SENTINEL_RELOAD_GUARD_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SentinelReloadGuard>;
    if (
      typeof parsed.remoteSentinelKey !== 'string' ||
      typeof parsed.attempts !== 'number' ||
      typeof parsed.lastReloadAt !== 'number'
    ) {
      return null;
    }
    return parsed as SentinelReloadGuard;
  } catch {
    return null;
  }
}

function writeSentinelReloadGuard(guard: SentinelReloadGuard) {
  try {
    window.sessionStorage.setItem(SENTINEL_RELOAD_GUARD_KEY, JSON.stringify(guard));
  } catch {
    // Intentionally ignore storage failures in private/blocked browsing contexts.
  }
}

function clearSentinelReloadGuard() {
  try {
    window.sessionStorage.removeItem(SENTINEL_RELOAD_GUARD_KEY);
  } catch {
    // Intentionally ignore storage failures in private/blocked browsing contexts.
  }
}

function detectIOSWebKit(): boolean {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent || '';
  const isiOSDevice =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isWebKit = /WebKit/i.test(ua);
  const isOtherEngine = /CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);

  return isiOSDevice && isWebKit && !isOtherEngine;
}

// Update-Banner anzeigen
function showUpdateBanner() {
  // Event für App.tsx auslösen (optional für Telemetrie/UI-Hinweis)
  window.dispatchEvent(new CustomEvent('appUpdateAvailable'));
}

// Funktion zum Prüfen des Offline-Status
export function isOffline() {
  return !navigator.onLine;
}

// Service Worker deregistrieren (für Entwicklungszwecke)
export async function unregisterServiceWorker() {
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.ready;
    await registration.unregister();
    window.location.reload();
  }
}
